import {
  Box,
  Divider,
  List,
  ListItem,
  ThemeProvider,
  Typography,
  IconButton,
  Skeleton,
} from "@mui/material";
import * as PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { listThemeDate } from "./component.styles";
import { formatNumber, getMerchantName } from "../utils";
import {
  TYPE_CREDIT,
  TYPE_DEBIT,
  TYPE_MERCHANT_RECLAIM,
} from "../store/actions/actionTypes";
import moment from "moment";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useNavigate } from "react-router-dom";
import { screenHeightPercentages } from "../utils/constants";
import { useTranslation } from "react-i18next";
import RecurringIcon from "../assets/logo/timer.svg";
import AppToolTip from "./AppTooltip";
import AppStatus from "./AppStatus";

function AppListDate({ getData, title = "Give me a title" }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listRef = useRef(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setCount] = useState(0);
  const [stopFetching, setStopFetching] = useState(true);
  const getHeight = () => {
    return (
      window.innerHeight -
      (window.innerHeight * screenHeightPercentages(window.innerHeight)) / 100
    );
  };
  const [height, setHeight] = useState(getHeight());

  // Adjust height on window resize
  useEffect(() => {
    window.addEventListener("resize", () => {
      setHeight(getHeight());
    });

    return () => window.removeEventListener("resize", () => { });
  }, []);

  // Fetch data whenever the page changes
  useEffect(() => {
    if (stopFetching) {
      fetchTransfers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchTransfers = async () => {
    if (loading) return; // Prevent duplicate fetches
    try {
      const offset = (page - 1) * limit;
      setLoading(true);
      const response = await getData(limit, offset);

      setCount(response?.result?.count);
      setData((prevData) => {
        const newOrders = response?.result?.orders || [];
        if (newOrders.length <= 0) setStopFetching(false);
        const combinedData = [...prevData, ...newOrders];
        const grouped = combinedData.reduce((acc, current) => {
          const { date, orders } = current;
          // Check if an entry for the same date already exists.
          const existing = acc.find((item) => item.date === date);

          if (existing) {
            // If the date exists, append the new orders to the existing orders.
            existing.orders = [...existing.orders, ...orders];
          } else {
            // If not, add the current item as is.
            acc.push({ date, orders });
          }
          return acc;
        }, []);
        return grouped;
      });
    } catch (error) {
      console.error("Error fetching transfers:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = useCallback(() => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    // If scrolled to 20% from the bottom, fetch more records
    if (scrollTop + clientHeight >= scrollHeight - 2 && !loading) {
      setPage((prevPage) => prevPage + 1);
    }
    if (scrollTop <= 10) {
      setStopFetching(true);
    }
  }, [loading]);

  // Attach scroll listener to the list container
  useEffect(() => {
    const list = listRef.current;
    if (list) list.addEventListener("scroll", handleScroll);
    return () => list && list.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const getByTitle = (value) => {
    if (value.type === TYPE_CREDIT) return getMerchantName(value.merchant);
    if (value.type === TYPE_DEBIT) return t("transfer");
    if (value.type === TYPE_MERCHANT_RECLAIM) {
      return `${t("Reclaim")} (${getMerchantName(value.merchant)})`;
    }
  };

  const renderSkeletons = () =>
    Array.from({ length: 3 }).map((_, index) => (
      <Box key={index}>
        <ListItem
          sx={{
            display: "block",
            paddingLeft: 0,
            paddingTop: "18px",
            paddingBottom: "18px",
          }}
        >
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
            width={"100%"}
          >
            <Box width={"50%"}>
              <Skeleton
                variant="text"
                width={"70%"}
                height={35}
                sx={{ backgroundColor: "#0a3c67" }}
              />
              <Skeleton
                variant="text"
                width={"90%"}
                height={30}
                sx={{ backgroundColor: "#0a3c67" }}
              />
            </Box>
            <Box
              display={"flex"}
              justifyContent={"flex-end"}
              flexDirection={"column"}
              alignItems={"end"}
              width={"50%"}
            >
              <Skeleton
                variant="text"
                width={"70%"}
                height={35}
                sx={{ backgroundColor: "#0a3c67" }}
              />
              <Skeleton
                variant="text"
                width={"90%"}
                height={30}
                sx={{ backgroundColor: "#0a3c67" }}
              />
            </Box>
          </Box>
        </ListItem>
        <Divider sx={{ border: "1px solid rgba(0, 136, 245, 0.15)" }} />
      </Box>
    ));

  return (
    <ThemeProvider theme={listThemeDate}>
      <List
        ref={listRef}
        sx={{
          boxShadow: "none",
          paddingTop: 1,
          maxHeight: height,
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        {data?.map((orders, index) => (
          <div key={`month-${index}`} >
            <Typography
              color="#FFFFFF"
              variant="h5"
              fontWeight={500}
              marginTop={1}
            >
              {orders.date}
            </Typography>
            {orders?.orders?.map((value, i) => (
              <div key={`order-${i}`}>
                <ListItem
                  aria-label="tracker"
                  aria-describedby="tranfer_List_item"
                  onClick={() => navigate(`/withdraw/details/${value.id}`)}
                  sx={{
                    display: "block",
                    paddingLeft: 0,
                    paddingTop: "18px",
                    paddingBottom: "18px",
                  }}
                >
                  <Box
                    aria-label="tracker"
                    aria-describedby="tranfer_List_item"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <Typography
                          color="#D4D4D8"
                          fontSize={16}
                          fontWeight={700}
                          marginRight={2}
                          aria-label="tracker"
                          aria-describedby="tranfer_List_item"
                        >
                          {getByTitle(value)}
                        </Typography>
                        {!!value?.recurringRuleId && (
                          <AppToolTip
                            enterTouchDelay={0}
                            title={t("recurring.transfers")}
                            placement="top"
                            arrow
                          >
                            <Box>
                              <RecurringIcon className="recurringIcon" />
                            </Box>
                          </AppToolTip>
                        )}
                      </Box>
                      {value?.descriptor && (
                        <Typography
                          color="#D4D4D8"
                          fontSize={14}
                          marginTop={1}
                          fontWeight={400}
                          aria-label="tracker"
                          aria-describedby="tranfer_List_item"
                        >
                          {value.descriptor}
                        </Typography>
                      )}
                      <Typography
                        color="#c3bebe"
                        fontSize={12}
                        marginTop={1}
                        fontWeight={400}
                        aria-label="tracker"
                        aria-describedby="tranfer_List_item"
                      >
                        {moment(value.createdAt).format("DD MMM YYYY")} at{" "}
                        {moment(value.createdAt).format("HH:MM A")}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"self-end"}
                    >
                      <Box display="flex" alignItems="center">
                        <Typography
                          color="#D4D4D8"
                          fontSize={16}
                          fontWeight={400}
                          aria-label="tracker"
                          aria-describedby="tranfer_List_item"
                        >
                          {value.type === TYPE_CREDIT
                            ? `+ $${formatNumber(value.amount)}`
                            : `- $${formatNumber(value.amount)}`}
                        </Typography>

                        <IconButton
                          sx={{ padding: 0, marginLeft: 1 }}
                          aria-label="tracker"
                          aria-describedby="tranfer_List_item"
                        >
                          <ArrowForwardIosIcon
                            aria-label="tracker"
                            aria-describedby="tranfer_List_item"
                            style={{ width: 10.66, color: "#D4D4D8" }}
                          />
                        </IconButton>
                      </Box>
                      <Box marginRight={2.5}>
                        {!value.onHold && (
                          <AppStatus
                            status={value.status}
                            donotShow={1}
                            logo={false}
                            fontSize={12}
                            progressColor={"#c3bebe"}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </ListItem>
                {orders.orders.length - 1 !== i && (
                  <Divider
                    sx={{ border: "1px solid rgba(0, 136, 245, 0.15)" }}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
        {loading && renderSkeletons()}
      </List>
      {!data.length && !loading && (
        <Box
          sx={{
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography fontWeight={400} fontSize={14} color="#DDDDDD">
            No {title}
          </Typography>
        </Box>
      )}
    </ThemeProvider>
  );
}

AppListDate.propTypes = {
  getData: PropTypes.func.isRequired,
  title: PropTypes.string,
};

export default AppListDate;
